/**
 * Drone roster — spec §7, §11.
 *
 * Assignment is two-step: pick a hex on the world map, then press Send on a
 * drone. A dropdown listing 18 hex coordinates would be unreadable, and the
 * map already shows which hexes still hold what.
 *
 * There is no mineral selector. Drones strip mine (SPEC-ERRATA E13), so the hex
 * IS the choice — a control offering to pick a mineral would be promising
 * something the simulation cannot deliver.
 *
 * ONE LINE PER DRONE. This used to be four lines and two buttons each, which
 * was tolerable at the three drones a game starts with and unreadable by the
 * twenty-odd it settles on — around 1,800 pixels of roster in a column that can
 * show 900. The cargo breakdown moved into the row's tooltip, since it is
 * detail the player wants occasionally rather than a number they scan.
 *
 * Rows are rebuilt only when the fleet size changes, not every tick, so buttons
 * stay clickable rather than being replaced out from under a click.
 */

import { MINERALS, MINERAL_IDS } from '../../sim/data';
import { hexKey } from '../../sim/hex';
import type { Drone, GameState, HexCoord } from '../../sim/types';
import { el, section, setText, type Issue, type Panel } from './panel';

interface DroneRow {
  root: HTMLDivElement;
  phase: HTMLSpanElement;
  target: HTMLSpanElement;
  load: HTMLSpanElement;
  send: HTMLButtonElement;
  auto: HTMLButtonElement;
}

/** Read the currently selected hex from the map, if any. */
export type SelectedHex = () => HexCoord | null;

/** Phases in the order a run goes through them, for the summary line. */
const PHASE_ORDER = ['idle', 'outbound', 'mining', 'inbound', 'unloading'] as const;

export function createDronePanel(issue: Issue, selectedHex: SelectedHex): Panel {
  const { root, body } = section('Drones');

  const summary = el('div', 'fleet-summary');
  const hint = el('div', 'hint', 'Click a hex, then Send.');

  // Fleet-wide auto-route. At twenty drones, setting this one row at a time is
  // twenty clicks to express a single intention.
  const bulk = el('div', 'button-row');
  const autoAll = el('button', 'action', 'Auto-route all');
  autoAll.title = 'Switch auto-route on for every drone';
  const autoNone = el('button', 'action', 'Manual all');
  autoNone.title = 'Switch auto-route off for every drone';
  bulk.append(autoAll, autoNone);

  // No build buttons here any more. Ordering a drone is the factory's assembly
  // bay taking an order over real ticks, and a button in the roster implied an
  // instant purchase — which is what it used to be.
  const list = el('div', 'drone-list');
  body.append(summary, hint, bulk, list);

  const rows = new Map<number, DroneRow>();
  let fleetIds: number[] = [];

  autoAll.addEventListener('click', () => {
    for (const id of fleetIds) {
      issue({ type: 'setDroneAutoReassign', droneId: id, autoReassign: true });
    }
  });
  autoNone.addEventListener('click', () => {
    for (const id of fleetIds) {
      issue({ type: 'setDroneAutoReassign', droneId: id, autoReassign: false });
    }
  });

  function buildRow_(drone: Drone): DroneRow {
    const rowRoot = el('div', 'drone-line');

    const name = el('span', 'drone-name', `#${drone.id} ${drone.class}`);
    const phase = el('span', 'drone-phase');
    const target = el('span', 'drone-target');
    const load = el('span', 'drone-load');

    const send = el('button', 'action drone-send', 'Send');
    send.addEventListener('click', () => {
      const hex = selectedHex();
      if (hex === null) return;
      issue({ type: 'assignDrone', droneId: drone.id, hex });
    });

    // "A" rather than "auto-route": at one line per drone the word does not
    // fit, and the lit state carries the meaning.
    const autoButton = el('button', 'action drone-auto', 'A');
    autoButton.title = 'Auto-route: pick the next hex without being asked';
    autoButton.addEventListener('click', () => {
      autoButton.dataset['on'] = autoButton.dataset['on'] === 'true' ? 'false' : 'true';
      issue({
        type: 'setDroneAutoReassign',
        droneId: drone.id,
        autoReassign: autoButton.dataset['on'] === 'true',
      });
    });

    rowRoot.append(name, phase, target, load, send, autoButton);

    return { root: rowRoot, phase, target, load, send, auto: autoButton };
  }

  return {
    root,
    update(state: GameState): void {
      // Rebuild only when the fleet changes. Rebuilding every tick would swap
      // the buttons out from under a player mid-click.
      if (rows.size !== state.drones.length) {
        rows.clear();
        list.replaceChildren();
        const ordered = [...state.drones].sort((a, b) => a.id - b.id);
        fleetIds = ordered.map((drone) => drone.id);
        for (const drone of ordered) {
          const row = buildRow_(drone);
          rows.set(drone.id, row);
          list.append(row.root);
        }
      }

      const counts = new Map<string, number>();

      for (const drone of state.drones) {
        counts.set(drone.phase, (counts.get(drone.phase) ?? 0) + 1);

        const row = rows.get(drone.id);
        if (row === undefined) continue;

        // A strip-mined hold carries several minerals. The total is what gets
        // scanned; the breakdown is a tooltip on the row.
        let held = 0;
        if (drone.cargo !== null) {
          for (const id of MINERAL_IDS) held += drone.cargo[id];
        }

        setText(row.phase, drone.phase);
        setText(row.target, drone.assignedHex === null ? '—' : hexKey(drone.assignedHex));
        setText(row.load, held > 0 ? String(held) : '');
        row.root.classList.toggle('idle', drone.phase === 'idle');

        if (drone.cargo === null || held === 0) {
          row.root.title = `${drone.class} drone, hold empty`;
        } else {
          const parts: string[] = [];
          for (const id of MINERAL_IDS) {
            const amount = drone.cargo[id];
            if (amount > 0) parts.push(`${MINERALS[id].label} ${amount}`);
          }
          row.root.title = `Carrying ${held}: ${parts.join(', ')}`;
        }

        row.auto.classList.toggle('toggle-on', drone.autoReassign);
        row.auto.dataset['on'] = String(drone.autoReassign);
      }

      const parts: string[] = [];
      for (const phase of PHASE_ORDER) {
        const count = counts.get(phase) ?? 0;
        if (count > 0) parts.push(`${count} ${phase}`);
      }
      setText(
        summary,
        `${state.drones.length} drone${state.drones.length === 1 ? '' : 's'} · ${parts.join(' · ')}`,
      );

      const hex = selectedHex();
      setText(hint, hex === null ? 'Click a hex, then Send.' : `Selected hex ${hexKey(hex)}`);
      for (const row of rows.values()) row.send.disabled = hex === null;
    },
  };
}
